import React from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.offsetParent !== null || element === document.activeElement
  );
}

/**
 * Mantém o teclado dentro de um diálogo modal: leva o foco para o primeiro
 * controle ao abrir, faz o Tab circular entre os controles do diálogo e devolve
 * o foco ao gatilho ao fechar.
 *
 * Sem isso, o Tab continua percorrendo a página atrás do overlay — os controles
 * de baixo seguem focáveis mesmo escondidos pelo blur.
 */
export function useDialogFocusTrap<T extends HTMLElement>(
  open: boolean,
  // "container": abre com o foco no próprio painel (que precisa de
  // tabIndex={-1}). Para diálogo de consentimento, em que o primeiro botão é
  // o aceite e um Enter repetido do gatilho aceitaria sem leitura.
  initialFocus: "first" | "container" = "first"
) {
  const containerRef = React.useRef<T | null>(null);

  React.useEffect(() => {
    if (!open) return;

    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const [first] = getFocusable(container);
    (initialFocus === "container" ? container : first || container).focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;

      const focusable = getFocusable(container);
      if (focusable.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (active === container || !container.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? lastElement : firstElement).focus();
        return;
      }

      if (event.shiftKey && active === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && active === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      previouslyFocused?.focus?.();
    };
  }, [open, initialFocus]);

  return containerRef;
}
